'use client'

import React, { useState } from 'react';
import { Avatar as MantineAvatar, MantineRadius, Skeleton } from '@mantine/core';
import { publicImageUrl } from '@/lib/staticAssets';

interface AvatarProps {
  userId: string;
  avatarId: string | null;
  size?: number;
  className?: string;
  alt?: string;
  placeholder?: React.ReactNode;
  radius?: MantineRadius;
  loading?: 'lazy' | 'eager';
  priority?: boolean; // For LCP images
}

export function Avatar({
  userId,
  avatarId,
  size = 80,
  className,
  radius,
  alt = 'Avatar',
  placeholder,
  loading = 'lazy',
  priority = false,
}: AvatarProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const finalAvatarId = avatarId || 'default';
  const defaultAvatar = publicImageUrl('images/default-avatar.svg');

  let src = defaultAvatar;
  if (finalAvatarId !== 'default') {
    src = `/api/files/avatar?userId=${encodeURIComponent(userId)}&avatarId=${encodeURIComponent(finalAvatarId)}&w=${size}&h=${size}`;
  }

  // Show skeleton for non-default avatars until loaded
  const showSkeleton = finalAvatarId !== 'default' && !imageLoaded;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {showSkeleton && (
        <Skeleton
          circle
          width={size}
          height={size}
          animate={false}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1,
            opacity: 0.3,
            backgroundColor: '#e0e0e0',
          }}
        />
      )}
      <MantineAvatar
        src={src}
        alt={alt}
        radius={radius}
        size={size}
        className={className}
        styles={{
          image: { 
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          },
          placeholder: { backgroundColor: 'transparent' },
          root: { 
            backgroundColor: 'transparent',
            aspectRatio: '1 / 1',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        imageProps={{
          loading,
          fetchPriority: priority ? 'high' : 'auto',
          decoding: 'async',
          onLoad: () => setImageLoaded(true),
          onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
            // Fallback to default avatar on error
            if (e.currentTarget.src !== defaultAvatar) {
              e.currentTarget.src = defaultAvatar;
            }
            setImageLoaded(true);
          },
        } as React.ImgHTMLAttributes<HTMLImageElement>}
      >
        {placeholder}
      </MantineAvatar>
    </div>
  );
}
